import { SaveOutlined } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useMemo, useState } from "react";
import { useBlocker } from "react-router-dom";
import api from "../../../../../services/api";
import InternoModalConfirmacaoSaida from "../../../../../components/modalSaindoDaPagina";
import ConfigVisibilidadeBlock from "../configVisibilidadeBlock";
import useStyles from "./styles";

// Tipo: 0: ASN, 1: CDNs, 2: Tráfego (mesmo mapeamento do schema.prisma)
const TIPO_CDN = 1;
const TIPO_CLASSE = 2;

// Seção "Configurações" do Flow: define quais CDNs e tipos de tráfego o
// cliente poderá visualizar no dashboard de Flow (3.8-flow), a partir do
// catálogo cadastrado em Administração > Configurações do Flow
// (administracao/configFlow, tabela ItensFlow) e da habilitação por cliente
// (tabela ConfigItensFlowCliente).
export default function ConfiguracoesFlow({
  setSnackbar,
  clienteId,
  isGrupoEconomico,
  onDirtyChange,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);

  // Mesmas cores de fundo usadas nos ícones dos cards de tráfego do
  // dashboard (3.0-dashboard/3.8-flow) e no cadastro do catálogo
  // (administracao/configFlow), garantindo contraste com os logos de CDN
  const coresMap = useMemo(
    () => ({
      azul:
        theme.palette.mode === "dark"
          ? theme.palette.cardGraphic.graphColor.blueMedium
          : theme.palette.cardGraphic.graphColor.blue,
      verde: theme.palette.cardGraphic.graphColor.green,
      vermelho: theme.palette.cardGraphic.graphColor.red,
      laranja: theme.palette.cardGraphic.graphColor.orangeMedium,
    }),
    [theme],
  );

  const [catalogo, setCatalogo] = useState([]);
  const [loadingCatalogo, setLoadingCatalogo] = useState(true);
  const [habilitados, setHabilitados] = useState(new Set());
  const [habilitadosSalvos, setHabilitadosSalvos] = useState(new Set());
  const [loadingSalvar, setLoadingSalvar] = useState(false);

  const fetchDados = async () => {
    setLoadingCatalogo(true);
    try {
      const [catalogoResponse, configResponse] = await Promise.all([
        api.get("/interno/find/itensFlow"),
        api.post("/interno/find/configItensFlowCliente", {
          clienteId,
        }),
      ]);

      const itens = (catalogoResponse?.data || []).filter(
        (item) => item.tipo === TIPO_CDN || item.tipo === TIPO_CLASSE,
      );
      const habilitadosIds = new Set(
        (configResponse?.data || []).map((item) => item.itenFlowId),
      );

      setCatalogo(itens);
      setHabilitados(habilitadosIds);
      setHabilitadosSalvos(new Set(habilitadosIds));
    } catch (error) {
      console.error(error);
      setSnackbar?.({
        children: "Erro ao buscar a configuração de visibilidade do Flow",
        severity: "error",
      });
    } finally {
      setLoadingCatalogo(false);
    }
  };

  useEffect(() => {
    if (clienteId && !isGrupoEconomico) {
      fetchDados();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, isGrupoEconomico]);

  const cdnOptions = useMemo(
    () =>
      catalogo
        .filter((item) => item.tipo === TIPO_CDN)
        .map((item) => ({
          key: item.id,
          label: item.identificador,
          image: item.imagem || null,
          color: coresMap[item.cor] || coresMap.azul,
        })),
    [catalogo, coresMap],
  );

  const trafegoOptions = useMemo(
    () =>
      catalogo
        .filter((item) => item.tipo === TIPO_CLASSE)
        .map((item) => ({
          key: item.id,
          label: item.identificador,
          image: item.imagem || null,
          color: coresMap[item.cor] || coresMap.azul,
        })),
    [catalogo, coresMap],
  );

  const selectionCdns = useMemo(
    () =>
      cdnOptions.reduce((acc, option) => {
        acc[option.key] = habilitados.has(option.key);
        return acc;
      }, {}),
    [cdnOptions, habilitados],
  );

  const selectionTrafego = useMemo(
    () =>
      trafegoOptions.reduce((acc, option) => {
        acc[option.key] = habilitados.has(option.key);
        return acc;
      }, {}),
    [trafegoOptions, habilitados],
  );

  const dirty = useMemo(() => {
    if (habilitados.size !== habilitadosSalvos.size) return true;
    for (const id of habilitados) {
      if (!habilitadosSalvos.has(id)) return true;
    }
    return false;
  }, [habilitados, habilitadosSalvos]);

  // Reporta o estado de alterações não salvas ao componente pai (2.5-flow),
  // que bloqueia a troca para a aba "Blocos" enquanto houver mudanças pendentes
  useEffect(() => {
    onDirtyChange?.(dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  // Confirmação ao sair da página com alterações de visibilidade não salvas
  const [confirmacaoSaidaPagina, setConfirmacaoSaidaPagina] = useState(false);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      currentLocation.pathname !== nextLocation.pathname && dirty,
  );

  useEffect(() => {
    if (blocker?.state === "blocked") {
      setConfirmacaoSaidaPagina(true);
    }
  }, [blocker?.state]);

  // Bloquear recarregar página, fechar aba, etc.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = ""; // Requerido por todos os browsers modernos
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [dirty]);

  const handleConfirmarSaida = () => {
    setConfirmacaoSaidaPagina(false);
    setHabilitados(new Set(habilitadosSalvos));
    blocker?.proceed?.();
  };

  const handleCancelarSaida = () => {
    setConfirmacaoSaidaPagina(false);
    blocker?.reset?.();
  };

  const handleToggleItem = (key) => {
    setHabilitados((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleToggleAll = (options, value) => {
    setHabilitados((prev) => {
      const next = new Set(prev);
      options.forEach((option) => {
        if (value) next.add(option.key);
        else next.delete(option.key);
      });
      return next;
    });
  };

  const handleCancelar = () => {
    setHabilitados(new Set(habilitadosSalvos));
  };

  const handleSalvar = async () => {
    setLoadingSalvar(true);
    try {
      await api.post("/interno/upsert/configItensFlowCliente", {
        clienteId,
        itensFlowIds: Array.from(habilitados),
      });
      setHabilitadosSalvos(new Set(habilitados));
      setSnackbar?.({
        children: "Configurações de visibilidade salvas com sucesso",
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      setSnackbar?.({
        children:
          error?.response?.data?.error ||
          "Não foi possível salvar as configurações de visibilidade",
        severity: "error",
      });
    } finally {
      setLoadingSalvar(false);
    }
  };

  if (isGrupoEconomico) {
    return (
      <Paper elevation={0} sx={styles.container}>
        <Box sx={styles.intro}>
          <Typography sx={styles.introTitle}>Visibilidade do Flow</Typography>
          <Typography sx={styles.introDescription}>
            A visibilidade do Flow é configurada por empresa. Selecione uma
            empresa específica do grupo para ajustar quais CDNs e tipos de
            tráfego ela poderá visualizar.
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (loadingCatalogo) {
    return (
      <Paper elevation={0} sx={styles.container}>
        <Box sx={styles.loadingState}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={styles.container}>
      <Box sx={styles.intro}>
        <Typography sx={styles.introTitle}>Visibilidade do Flow</Typography>
        <Typography sx={styles.introDescription}>
          Escolha quais CDNs e tipos de tráfego este cliente poderá visualizar
          no dashboard de Flow.
        </Typography>
      </Box>

      {catalogo.length === 0 ? (
        <Box sx={styles.emptyState}>
          <Typography sx={styles.emptyTitle}>
            Nenhum CDN ou classe cadastrado
          </Typography>
          <Typography sx={styles.emptyDescription}>
            Cadastre os itens em Administração &gt; geral &gt; Configurações do
            Flow para liberar as opções de visibilidade aqui.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={styles.blocksContainer}>
            <ConfigVisibilidadeBlock
              title="CDNs"
              description="Provedores de conteúdo exibidos nos cards de consumo"
              options={cdnOptions}
              selection={selectionCdns}
              onToggleItem={handleToggleItem}
              onToggleAll={handleToggleAll}
            />
            <ConfigVisibilidadeBlock
              title="Tipos de tráfego"
              description="Classes de interface exibidas nos cards de tráfego"
              options={trafegoOptions}
              selection={selectionTrafego}
              onToggleItem={handleToggleItem}
              onToggleAll={handleToggleAll}
            />
          </Box>

          <Box sx={styles.footer}>
            <Box sx={styles.footerActions}>
              <Button
                variant="outlined"
                disabled={loadingSalvar || !dirty}
                onClick={handleCancelar}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                startIcon={
                  loadingSalvar ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveOutlined />
                  )
                }
                disabled={loadingSalvar || !dirty}
                onClick={handleSalvar}
              >
                Salvar configurações
              </Button>
            </Box>
          </Box>
        </>
      )}

      <InternoModalConfirmacaoSaida
        openConfirmacao={confirmacaoSaidaPagina}
        setOpenConfirmacao={setConfirmacaoSaidaPagina}
        execute={handleConfirmarSaida}
        cancel={handleCancelarSaida}
        title={"Atenção"}
        severity={"warning"}
        confirmText={"Continuar"}
        headerText={"Você está prestes a sair da página."}
        contentText={
          "As alterações de visibilidade não salvas serão perdidas, deseja continuar?"
        }
      />
    </Paper>
  );
}
